/**
 * Data layer for the adaptive water-entry surface.
 *
 * All completion / fill-map / pH-dial state is computed CLIENT-SIDE from raw
 * readings (via @/lib/waterModel) — no bespoke backend endpoint. Readings come
 * from the existing cursor-paginated GET /readings; pond + DOC come from the
 * entry checklist (DOC there is relative to target_date, so we derive
 * stocking_date = date − doc for the fill-map range).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toNumberOrNull } from '@/lib/number';
import { qk } from '@/lib/queryKeys';
import { normalizeChecklistResponse } from '@/types/entry';
import type { ReadingSlot, ChecklistResponse } from '@/types/entry';
import type {
  ChecklistResponse as ApiChecklistResponse,
  WaterReadingResponse,
} from '@/types/generated/openapi';
import { CLEAR_TO_BOTTOM, GRID_PARAMS, OBSERVATION_PARAMS } from '@/lib/waterModel';
import type { ReadingsBySlot } from '@/lib/waterModel';

interface ListResp<T> {
  data: T[];
  meta?: { cursor?: string | null; has_more?: boolean | null };
}

const VALUE_PARAMS = [...GRID_PARAMS, ...OBSERVATION_PARAMS];

export type SlotValues = Record<string, number | string | null>;
/** One pond/date: slot → { reading id (if it exists), its column values }. */
export type DayReadings = Partial<Record<ReadingSlot, { id: string; values: SlotValues }>>;

/**
 * The single parse point from wire → grid values. The API serialises Decimal
 * columns as strings ("8.10") and the grid only renders numbers, so numeric
 * params are parsed here rather than per cell.
 */
export function readingToValues(r: WaterReadingResponse): SlotValues {
  const row = r as unknown as Record<string, number | string | null | undefined>;
  const v: SlotValues = {};
  for (const p of VALUE_PARAMS) {
    const raw = row[p.dbColumn];
    v[p.dbColumn] = p.type === 'number' ? (toNumberOrNull(raw) ?? null) : (raw ?? null);
  }
  if (v.transparency_cm === null && r.transparency_clear_to_bottom === true) v.transparency_cm = CLEAR_TO_BOTTOM;
  v.notes = r.notes ?? null;
  // Words from before the number pairs: no longer entered, still shown when no numbers exist.
  v.calmag = r.calmag ?? null;
  return v;
}

export function groupBySlot(readings: WaterReadingResponse[]): DayReadings {
  const out: DayReadings = {};
  for (const r of readings) {
    out[r.reading_slot as ReadingSlot] = { id: r.id, values: readingToValues(r) };
  }
  return out;
}

/** date → (slot → values), for the cycle fill-map + all-ponds day view. */
export function groupByDate(readings: WaterReadingResponse[]): Map<string, ReadingsBySlot> {
  const m = new Map<string, ReadingsBySlot>();
  for (const r of readings) {
    const day = m.get(r.reading_date) ?? {};
    day[r.reading_slot as ReadingSlot] = readingToValues(r);
    m.set(r.reading_date, day);
  }
  return m;
}

/** cycle_id → (slot → values), for the all-ponds grid on one date. */
export function groupByCycle(readings: WaterReadingResponse[]): Map<string, ReadingsBySlot> {
  const m = new Map<string, ReadingsBySlot>();
  for (const r of readings) {
    const c = m.get(r.cycle_id) ?? {};
    c[r.reading_slot as ReadingSlot] = readingToValues(r);
    m.set(r.cycle_id, c);
  }
  return m;
}

async function fetchReadingsPaged(
  farmId: string,
  params: Record<string, string>
): Promise<WaterReadingResponse[]> {
  const out: WaterReadingResponse[] = [];
  let cursor: string | undefined;
  // Cursor loop — list endpoint caps limit at 100, a cycle can hold ~600 rows.
  for (let guard = 0; guard < 50; guard++) {
    const resp = await api.get<ListResp<WaterReadingResponse>>(
      `/api/v1/farms/${farmId}/readings`,
      { params: { ...params, limit: '100', ...(cursor ? { cursor } : {}) } }
    );
    out.push(...(resp.data ?? []));
    if (resp.meta?.has_more && resp.meta.cursor) {
      cursor = resp.meta.cursor;
    } else {
      break;
    }
  }
  return out;
}

export function usePondsForDate(farmId: string | undefined, date: string) {
  return useQuery<ChecklistResponse>({
    queryKey: ['entry-ponds', farmId, date],
    queryFn: async () => {
      const r = await api.get<{ data: ApiChecklistResponse }>(
        `/api/v1/farms/${farmId}/entry/checklist`,
        { params: { target_date: date } }
      );
      return normalizeChecklistResponse(r.data);
    },
    enabled: !!farmId,
    staleTime: 15_000,
  });
}

/**
 * The checklist plus preparing ponds, for phone Today only. Its own key so the
 * desktop entry pages, which assume every pond has an active crop, never see them.
 */
export function usePondsForDateWithPreparing(farmId: string | undefined, date: string) {
  return useQuery<ChecklistResponse>({
    queryKey: ['entry-ponds', farmId, date, 'with-preparing'],
    queryFn: async () => {
      const r = await api.get<{ data: ApiChecklistResponse }>(
        `/api/v1/farms/${farmId}/entry/checklist`,
        { params: { target_date: date, include_preparing: 'true' } }
      );
      return normalizeChecklistResponse(r.data);
    },
    enabled: !!farmId,
    staleTime: 15_000,
  });
}

export function useDayReadings(
  farmId: string | undefined,
  cycleId: string | undefined,
  date: string
) {
  return useQuery({
    queryKey: qk.readings.day(farmId, cycleId ?? '', date),
    queryFn: () =>
      fetchReadingsPaged(farmId!, { cycle_id: cycleId!, start_date: date, end_date: date }),
    enabled: !!farmId && !!cycleId,
  });
}

export function useCycleReadings(
  farmId: string | undefined,
  cycleId: string | undefined,
  startDate: string | undefined,
  endDate: string
) {
  return useQuery({
    queryKey: ['entry-cycle-readings', farmId, cycleId, startDate, endDate],
    queryFn: () =>
      fetchReadingsPaged(farmId!, {
        cycle_id: cycleId!,
        start_date: startDate!,
        end_date: endDate,
      }),
    enabled: !!farmId && !!cycleId && !!startDate,
    staleTime: 15_000,
  });
}

export function useAllPondsDayReadings(farmId: string | undefined, date: string) {
  return useQuery({
    queryKey: ['entry-allponds-readings', farmId, date],
    queryFn: () => fetchReadingsPaged(farmId!, { start_date: date, end_date: date }),
    enabled: !!farmId,
    staleTime: 15_000,
  });
}
